import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, AlertTriangle, Clock, Calendar, Phone, Mail, MapPin, MessageSquare } from "lucide-react";
import { CONTACT, CONTACTS, SERVICES } from "../../data/hseData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const URGENCIES = [
  { value: "normal", label: "Normal", desc: "Avaliação técnica padrão", Icon: Clock },
  { value: "programada", label: "Programada", desc: "Parada / projeto planejado", Icon: Calendar },
  { value: "emergencial", label: "Emergencial", desc: "Atendimento 24/7", Icon: AlertTriangle },
];

const INITIAL = {
  nome: "", empresa: "", telefone: "", email: "",
  cidade_estado: "", servico_interesse: "", descricao: "", urgencia: "normal",
};

export const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildWhatsAppMessage = (data) => {
    const lines = [
      `*Nova solicitação - Site HSE*`,
      `Nome: ${data.nome}`,
      data.empresa ? `Empresa: ${data.empresa}` : null,
      `Telefone: ${data.telefone}`,
      `E-mail: ${data.email}`,
      data.cidade_estado ? `Cidade/UF: ${data.cidade_estado}` : null,
      data.servico_interesse ? `Serviço de interesse: ${data.servico_interesse}` : null,
      `Urgência: ${data.urgencia}`,
      ``,
      `Descrição:`,
      data.descricao,
    ].filter(Boolean);
    return encodeURIComponent(lines.join("\n"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone || !form.email || !form.descricao) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(
        "Solicitação registrada! A HSE entrará em contato para entender sua necessidade e propor a melhor solução técnica."
      );
      // open WhatsApp with message prefilled
      const phone = CONTACT.whatsappLink.replace(/\D/g, "").replace(/^https/, "");
      const wa = `https://wa.me/${phone}?text=${buildWhatsAppMessage(form)}`;
      window.open(wa, "_blank");
      setForm(INITIAL);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar agora. Tente novamente.";
      toast.error(typeof msg === "string" ? msg : "Erro ao enviar formulário.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0B1B2D] border border-white/15 focus:border-[#2FA36B] outline-none text-white font-body text-sm px-4 py-3.5 transition-colors placeholder:text-white/30";

  return (
    <section id="contato" data-testid="section-contato" className="relative py-24 sm:py-32 bg-[#081726]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Heading + info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#2FA36B]" />
              <span className="font-condensed uppercase text-xs tracking-[0.4em] text-[#2FA36B] font-bold">
                Contato
              </span>
            </div>
            <h2 className="font-condensed font-bold uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight">
              Solicite uma
              <span className="text-[#2FA36B]"> avaliação técnica</span>.
            </h2>
            <p className="mt-6 text-white/65 font-body text-base leading-relaxed max-w-md">
              Conte sua necessidade. Nossa equipe técnica retorna com a melhor solução para sua operação.
            </p>

            <div className="mt-10 space-y-5">
              {CONTACTS.map((c, i) => (
                <div key={c.email} data-testid={`contact-person-${i}`} className="border border-white/10 bg-[#12283F] p-5">
                  <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-[#2FA36B] font-bold mb-2">{c.role}</p>
                  <p className="font-condensed font-bold text-white text-lg leading-tight mb-3">{c.name}</p>
                  <div className="space-y-2 font-body text-sm text-white/85">
                    <a href={`https://wa.me/${c.phoneRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#2FA36B]">
                      <MessageSquare size={14} className="text-[#2FA36B]" /> {c.phone}
                    </a>
                    <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-[#2FA36B]">
                      <Mail size={14} className="text-[#2FA36B]" /> {c.email}
                    </a>
                  </div>
                </div>
              ))}
              <div data-testid="contact-address" className="flex items-start gap-4 pt-2">
                <div className="w-11 h-11 border border-white/15 flex items-center justify-center">
                  <MapPin size={18} className="text-[#2FA36B]" />
                </div>
                <div>
                  <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-white/40 font-bold">Endereço</p>
                  <p className="font-body text-white text-base">{CONTACT.address}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7 border border-white/10 bg-[#12283F] p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Nome *</label>
                <input data-testid="input-nome" required className={inputCls} value={form.nome} onChange={handle("nome")} placeholder="Seu nome completo" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Empresa</label>
                <input data-testid="input-empresa" className={inputCls} value={form.empresa} onChange={handle("empresa")} placeholder="Razão social" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Telefone / WhatsApp *</label>
                <input data-testid="input-telefone" required className={inputCls} value={form.telefone} onChange={handle("telefone")} placeholder="(00) 00000-0000" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">E-mail *</label>
                <input data-testid="input-email" required type="email" className={inputCls} value={form.email} onChange={handle("email")} placeholder="voce@empresa.com.br" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Cidade / Estado</label>
                <input data-testid="input-cidade" className={inputCls} value={form.cidade_estado} onChange={handle("cidade_estado")} placeholder="São Paulo / SP" />
              </div>
              <div>
                <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Serviço de interesse</label>
                <select data-testid="input-servico" className={inputCls} value={form.servico_interesse} onChange={handle("servico_interesse")}>
                  <option value="">Selecione...</option>
                  {SERVICES.map((s) => (
                    <option key={s.id} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-2">Descrição da necessidade *</label>
              <textarea data-testid="input-descricao" required rows={5} className={inputCls} value={form.descricao} onChange={handle("descricao")} placeholder="Descreva a operação, escopo, prazo e principais riscos." />
            </div>

            {/* Urgency selector */}
            <div className="mt-6">
              <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-white/50 font-bold mb-3">Urgência *</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {URGENCIES.map((u) => {
                  const active = form.urgencia === u.value;
                  return (
                    <button
                      type="button"
                      key={u.value}
                      data-testid={`urgency-${u.value}`}
                      onClick={() => setForm({ ...form, urgencia: u.value })}
                      className={`text-left p-4 border transition-all ${
                        active
                          ? "border-[#2FA36B] bg-[#2FA36B]/10"
                          : "border-white/15 hover:border-white/40 bg-[#0B1B2D]"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <u.Icon size={16} className={active ? "text-[#2FA36B]" : "text-white/60"} />
                        <span className="font-condensed font-bold uppercase text-sm tracking-tight text-white">
                          {u.label}
                        </span>
                      </div>
                      <span className="font-body text-xs text-white/55 block">{u.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#37B87A] disabled:opacity-60 text-white px-8 py-5 transition-colors"
            >
              {loading ? "Enviando..." : "Solicitar avaliação técnica"}
              <Send size={16} />
            </button>

            <p className="mt-5 font-body text-xs text-white/45 leading-relaxed">
              Ao enviar, você concorda em ser contatado pela equipe HSE. A HSE entrará em contato para entender sua necessidade e propor a melhor solução técnica para sua operação.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
